import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {MembershipFee} from '../../model/membership-fee';
import {MemberStateService} from '../../services/member-state.service';
import {FormatService} from '../../services/format.service';
import {MembershipOverview} from '../../model/membership-overview';
import {Member} from '../../model/member';
import {LocalizedText} from '../../model/localized-text';
import {map, switchMap, take} from 'rxjs/operators';
import {MemberService} from '../../services/member.service';
import {Observable} from 'rxjs';
import {ContributionsOverviewComponent} from './contributions-overview/contributions-overview.component';
import {LoaderComponent} from '../shared/loader/loader.component';
import {UserContextService} from '../../services/user-context.service';
import {ViewState, toViewState} from '../../model/view-state';
import {FIRST_EURO_YEAR, isEuroMembershipFee} from '../../view/membership-fee.view';

@Component({
    selector: 'myabvv-contribution',
    templateUrl: './contribution.component.html',
    standalone: true,
    imports: [AsyncPipe, TranslateModule, ContributionsOverviewComponent, LoaderComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContributionComponent implements OnInit {
  private memberService = inject(MemberService);
  private memberStateService = inject(MemberStateService);
  private formatService = inject(FormatService);
  private userContext = inject(UserContextService);


  state$: Observable<ViewState<{memberShips: MembershipOverview; memberShipFees: MembershipFee[]; hasHiddenPreEuroYears: boolean}>>;
  startYearOfEURO = FIRST_EURO_YEAR;

  ngOnInit() {
    this.loadContributions();
  }

  loadContributions() {
    const isInternalUser = this.userContext.isInternalUser();

    let memberInss: string;

    this.state$ = this.memberStateService.getMember().pipe(
      take(1),
      switchMap((m: Member) => {
        memberInss = m.personalInformation.inss;
        return isInternalUser
          ? this.memberService.getMemberShip(memberInss)
          : this.memberService.getMyMemberShip();
      }),
      map((res: MembershipOverview) => {
        // Pre-euro years are amounts in Belgian francs: hidden on purpose, and flagged so the page can say so
        if (!res?.contributionYears) {
          return { membershipOverview: res, hasHiddenPreEuroYears: false };
        }
        const euroYears = res.contributionYears
          .filter(contributionsByYear => contributionsByYear.yearOfContribution >= this.startYearOfEURO);
        return {
          membershipOverview: { ...res, contributionYears: euroYears },
          hasHiddenPreEuroYears: euroYears.length < res.contributionYears.length
        };
      }),
      switchMap(({ membershipOverview, hasHiddenPreEuroYears }) => {
        const fees$ = isInternalUser
          ? this.memberService.getMemberShipFees(memberInss)
          : this.memberService.getMyMemberShipFees();
        return fees$.pipe(
          map((fees: MembershipFee[]) => ({
            memberShips: membershipOverview,
            memberShipFees: fees.filter(isEuroMembershipFee),
            hasHiddenPreEuroYears
          }))
        );
      }),
      toViewState()
    );
  }

  getTranslatedData(array: LocalizedText[] | null | undefined): string {
    return this.formatService.getTranslatedData(array);
  }
}
